/**
 * Airtable Repository
 *
 * OAuth status/connect/disconnect, workspace listing, and sync dispatch/poll
 * for the dashboard Airtable integration. All calls go through apiService with
 * the JWT authToken (no raw fetch).
 */

import { createRepository } from 'data/utils/createRepository';
import { getApiUrl } from 'data/services/apiConstants';
import { get, post, patch, del } from 'data/services/apiService';

// ============================================
// Create Repository
// ============================================
const airtableRepository = createRepository({
	endpoint: 'airtable/oauth/status',
	name: 'airtable',
	statePath: 'airtable',

	customMethods: {
		/**
		 * Get the current Airtable OAuth connection status
		 * Usage: actions.getOAuthStatus()
		 */
		getOAuthStatus: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/airtable/oauth/status`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Start the Airtable OAuth authorization flow
		 * Usage: actions.startOAuth()
		 */
		startOAuth: ({ authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/airtable/oauth/authorize`,
				data: {},
				authToken,
				dispatch,
			});
		},

		/**
		 * Disconnect the Airtable OAuth integration
		 * Usage: actions.disconnect()
		 */
		disconnect: ({ authToken, dispatch }) => {
			return del({
				url: `${getApiUrl()}/api/airtable/oauth`,
				authToken,
				dispatch,
			});
		},

		/**
		 * List the available Airtable workspaces
		 * Usage: actions.getWorkspaces()
		 */
		getWorkspaces: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/airtable/workspaces`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Dispatch an Airtable sync run
		 * Usage: actions.runSync({ payload: { ... } })
		 */
		runSync: ({ payload, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/airtable/sync`,
				data: payload,
				authToken,
				dispatch,
			});
		},

		/**
		 * Poll the status of the Airtable sync
		 * Usage: actions.getSyncStatus()
		 */
		getSyncStatus: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/airtable/sync/status`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get the user's syncable properties
		 * Usage: actions.getSyncableProperties()
		 * Returns { success, data: { properties: [...], pagination } }
		 */
		getSyncableProperties: ({ authToken, dispatch }) =>
			get({
				url: `${getApiUrl()}/api/airtable/sync/properties`,
				authToken,
				dispatch,
			}),

		/**
		 * Save the unattended sync schedule and property set (no sync runs)
		 * Usage: actions.saveSchedule({ payload: { frequency, propertyIds } })
		 */
		saveSchedule: ({ payload, authToken, dispatch }) => {
			return patch({
				url: `${getApiUrl()}/api/airtable/sync/schedule`,
				data: payload,
				authToken,
				dispatch,
			});
		},
	},
});

// ============================================
// Exports
// ============================================
export const {
	name,
	statePath,
	thunks: airtableThunks,
	reducer: airtableReducer,
	actions: airtableActions,
	types: airtableTypes,
	selectors: airtableSelectors,
	useHook: useAirtable,
} = airtableRepository;

export default airtableRepository;
